/**
 * Workflow graph rules, shared by the server (which refuses to run a broken
 * graph and walks a running one) and the web editor (which shows the same
 * problems on the chart before anyone presses Run).
 *
 * A graph is steps joined by links. Exactly one Start step begins a run;
 * links run forward only (no loops: "go back and redo" is a retry, not a
 * cycle); every step must be reachable from Start; step names are distinct
 * and never "Input", so a `{{Step name}}` reference means one thing. An
 * Ingestion step's fields are named too, and `{{Step name.Field name}}` reads
 * one of them.
 *
 * Walking a run (`planWorkflowRun`) is pure: given the graph and each step's
 * state, it says which steps are ready, which are skipped, and how the run
 * stands. A link is active when its source step succeeded and chose that
 * outlet (a step with no outlet choice takes all of them). A step is ready
 * once every link into it is settled and at least one is active; it is
 * skipped when every link into it settled inactive (a branch not taken). A
 * step behind a failed or cancelled one is never started: it stays pending,
 * and the run ends failed once nothing else is moving.
 *
 * Types are structural so the module needs nothing but itself; the wire
 * schemas live in `@amoreg/contracts` (`workflows.ts`).
 *
 * @module workflowGraph
 */

export interface WorkflowGraphNodeLike {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly config?: unknown;
}

export interface WorkflowGraphEdgeLike {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly sourceHandle?: string | undefined;
}

export interface WorkflowGraphLike {
  readonly nodes: ReadonlyArray<WorkflowGraphNodeLike>;
  readonly edges: ReadonlyArray<WorkflowGraphEdgeLike>;
}

export interface WorkflowGraphIssueLike {
  readonly code: string;
  readonly message: string;
  readonly nodeId?: string;
  readonly edgeId?: string;
}

export type WorkflowStepState =
  | "pending"
  | "running"
  | "waiting"
  | "succeeded"
  | "failed"
  | "skipped"
  | "cancelled";

export type WorkflowRunState = "running" | "waiting" | "succeeded" | "failed";

export interface WorkflowStepFacts {
  readonly status: WorkflowStepState;
  /**
   * The outlets a succeeded step chose. `null` (or absent) means every
   * outlet: ordinary steps have one and always take it.
   */
  readonly chosenHandles?: ReadonlyArray<string> | null;
}

const issue = (code: string, message: string, at: { nodeId?: string; edgeId?: string } = {}) =>
  ({ code, message, ...at }) satisfies WorkflowGraphIssueLike;

const promptInstructions = (node: WorkflowGraphNodeLike): string => {
  const config = node.config as { readonly instructions?: unknown } | undefined;
  return typeof config?.instructions === "string" ? config.instructions : "";
};

const browserTaskId = (node: WorkflowGraphNodeLike): string => {
  const config = node.config as { readonly taskId?: unknown } | undefined;
  return typeof config?.taskId === "string" ? config.taskId : "";
};

/** A Condition step's outlets: the run leaves it by one of these. */
export const CONDITION_OUTLETS = ["yes", "no"] as const;

/**
 * Whether a step leaves by Yes or No rather than one plain outlet: a
 * Condition, and a Compare step (Yes: it found exceptions, AMO-301).
 */
export const workflowStepBranches = (type: string): boolean => type === "condition" || type === "compare";

/** The most rules one Compare step checks (`WORKFLOW_LIMITS.compareRules`). */
export const MAX_COMPARE_RULES = 20;

/** A Compare step's two tables, key and rules, read structurally; blanks where they are missing. */
export function workflowCompareConfig(node: WorkflowGraphNodeLike): {
  readonly left: { readonly step: string; readonly table: string };
  readonly right: { readonly step: string; readonly table: string };
  readonly key: string;
  readonly rightKey: string;
  readonly rules: ReadonlyArray<{ readonly column: string; readonly against: string; readonly check: string; readonly limit: number }>;
  readonly missing: boolean;
} {
  const config = (node.config ?? {}) as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
  const ref = (value: unknown) => {
    const record = (value ?? {}) as Record<string, unknown>;
    return { step: text(record.step), table: text(record.table) };
  };
  const rules = Array.isArray(config.rules) ? config.rules : [];
  return {
    left: ref(config.left),
    right: ref(config.right),
    key: text(config.key),
    rightKey: text(config.rightKey),
    rules: rules.map((entry: unknown) => {
      const rule = (entry ?? {}) as Record<string, unknown>;
      return {
        column: text(rule.column),
        against: text(rule.against),
        check: text(rule.check),
        limit: typeof rule.limit === "number" && Number.isFinite(rule.limit) ? rule.limit : 0,
      };
    }),
    missing: config.missing === true,
  };
}

/** Whether a Condition step says what it decides on: its question, or its text to look for. */
const conditionIsSet = (node: WorkflowGraphNodeLike): boolean => {
  const config = node.config as { readonly mode?: unknown; readonly question?: unknown; readonly text?: unknown } | undefined;
  const value = config?.mode === "contains" ? config?.text : config?.question;
  return typeof value === "string" && value.trim() !== "";
};

/**
 * The people an Approval step names, as lower-cased addresses, each once.
 * Anything that is not a string is dropped; an address that does not look
 * like one stays, so the graph check can name it.
 */
export function workflowApproverEmails(node: WorkflowGraphNodeLike): ReadonlyArray<string> {
  return configEmails(node, "approverEmails");
}

/** The people a manual step names (AMO-307), read as Approval's approvers are; none means the run's starter. */
export function workflowChecklistEmails(node: WorkflowGraphNodeLike): ReadonlyArray<string> {
  return configEmails(node, "assigneeEmails");
}

/** Who may mark a manual step done: the people it names, or the person who started the run when it names nobody. */
export const workflowChecklistPeople = (node: WorkflowGraphNodeLike, starterEmail: string | null): ReadonlyArray<string> => {
  const named = workflowChecklistEmails(node);
  return named.length > 0 ? named : starterEmail ? [starterEmail] : [];
};

/** The people a Notify or Send email step names, read as Approval's approvers are. */
export function workflowRecipientEmails(node: WorkflowGraphNodeLike): ReadonlyArray<string> {
  return configEmails(node, "recipientEmails");
}

const configEmails = (node: WorkflowGraphNodeLike, key: "approverEmails" | "recipientEmails" | "assigneeEmails"): ReadonlyArray<string> => {
  const config = node.config as Readonly<Record<string, unknown>> | undefined;
  const value = config?.[key];
  const raw: ReadonlyArray<unknown> = Array.isArray(value) ? value : [];
  const emails = raw.flatMap((entry) => (typeof entry === "string" && entry.trim() ? [entry.trim().toLowerCase()] : []));
  return [...new Set(emails)];
};

/** The most approvers one step names (the wire limit, `WORKFLOW_LIMITS.approvers`). */
export const MAX_APPROVERS = 20;
/** The most people one Notify or Send email step names (`WORKFLOW_LIMITS.recipients`). */
export const MAX_RECIPIENTS = 50;

/** The most sources one Intake step reads (`WORKFLOW_LIMITS.intakeSources`). */
export const MAX_INTAKE_SOURCES = 10;

/** Mail an email source reads by default, and at most: the last this many days. */
export const MAIL_DAYS_DEFAULT = 30;
export const MAIL_DAYS_MAX = 90;
/** The mailbox apps an email source can name (`WORKFLOW_MAIL_APPS`). */
export const MAIL_APPS = ["gmail", "outlook"] as const;
export type MailApp = (typeof MAIL_APPS)[number];

export interface WorkflowMailFilterLike {
  readonly from: string;
  readonly subject: string;
  readonly label: string;
  readonly days: number;
  /** Read the matched messages' attached files too (AMO-302). */
  readonly attachments?: boolean | undefined;
  /** Only attached files whose name contains this, ignoring case; empty reads every one. */
  readonly attachmentName?: string | undefined;
}

export interface WorkflowIntakeSourceLike {
  readonly kind: string;
  readonly location: string;
  readonly picks: ReadonlyArray<{ readonly id: string; readonly name: string; readonly isFolder?: boolean; readonly path?: string }>;
  readonly query: string;
  /** An email source's filter, its words trimmed and its days within 1..90; absent on other sources. */
  readonly mail?: WorkflowMailFilterLike;
}

const mailFilter = (raw: unknown): WorkflowMailFilterLike => {
  const mail = (raw ?? {}) as Partial<Record<keyof WorkflowMailFilterLike, unknown>>;
  const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
  const days = typeof mail.days === "number" && Number.isFinite(mail.days) ? Math.round(mail.days) : MAIL_DAYS_DEFAULT;
  return {
    from: text(mail.from),
    subject: text(mail.subject),
    label: text(mail.label),
    days: Math.min(MAIL_DAYS_MAX, Math.max(1, days)),
    ...(mail.attachments === true ? { attachments: true, attachmentName: text(mail.attachmentName) } : {}),
  };
};

/**
 * Whether an email filter is narrowed by sender, subject or label, as every
 * email search must be. A value counts only with a letter or digit in it:
 * one of nothing but quotes or punctuation adds no clause to the search.
 */
export const mailFilterIsNarrow = (mail: WorkflowMailFilterLike): boolean =>
  [mail.from, mail.subject, mail.label].some((value) => /[\p{L}\p{N}]/u.test(value));

/** The mailbox apps an Intake step's email sources read, each once. */
export function workflowMailApps(node: WorkflowGraphNodeLike): ReadonlyArray<MailApp> {
  if (node.type !== "intake") return [];
  const apps = workflowIntakeSources(node).flatMap((source) =>
    source.kind === "email" && (MAIL_APPS as ReadonlyArray<string>).includes(source.location) ? [source.location as MailApp] : [],
  );
  return [...new Set(apps)];
}

/** An Intake step's sources; anything that is not one is dropped. */
export function workflowIntakeSources(node: WorkflowGraphNodeLike): ReadonlyArray<WorkflowIntakeSourceLike> {
  const config = node.config as { readonly sources?: unknown } | undefined;
  const raw: ReadonlyArray<unknown> = Array.isArray(config?.sources) ? config.sources : [];
  return raw.flatMap((entry) => {
    const source = entry as Partial<Record<keyof WorkflowIntakeSourceLike, unknown>> | null;
    if (!source || typeof source.kind !== "string") return [];
    return [
      {
        kind: source.kind,
        location: typeof source.location === "string" ? source.location.trim() : "",
        picks: Array.isArray(source.picks) ? (source.picks as WorkflowIntakeSourceLike["picks"]) : [],
        query: typeof source.query === "string" ? source.query.trim() : "",
        ...(source.kind === "email" ? { mail: mailFilter(source.mail) } : {}),
      },
    ];
  });
}

/** Why an Intake step's sources cannot run; null when they can. */
const intakeIssue = (node: WorkflowGraphNodeLike): WorkflowGraphIssueLike | null => {
  const sources = workflowIntakeSources(node);
  const at = { nodeId: node.id };
  if (sources.length === 0) return issue("empty-intake", "Add a source for this step to read.", at);
  if (sources.length > MAX_INTAKE_SOURCES) return issue("too-many-sources", `Read at most ${MAX_INTAKE_SOURCES} sources in one step.`, at);
  for (const source of sources) {
    if (source.kind !== "email") continue;
    if (!(MAIL_APPS as ReadonlyArray<string>).includes(source.location)) return issue("no-mailbox", "Choose Gmail or Outlook for each email source.", at);
    // The privacy policy promises every mailbox search is narrowed: never the whole mailbox.
    if (!source.mail || !mailFilterIsNarrow(source.mail)) {
      return issue("broad-email", "Narrow each email search by sender, subject or label.", at);
    }
  }
  if (sources.some((source) => source.kind !== "email" && source.picks.length === 0 && source.query === "")) {
    return issue("empty-source", "Pick documents or write a search for every source.", at);
  }
  if (sources.some((source) => source.kind === "space" && source.location === "")) {
    return issue("no-space", "Choose the space each Space files source reads.", at);
  }
  return null;
};

/** The destination steps (AMO-166): where a run's results go. */
export const WORKFLOW_DESTINATION_TYPES = ["chat", "notify", "save-file", "email"] as const;

/** The most fields one Ingestion step reads (the wire limit, `WORKFLOW_LIMITS.ingestionFields`). */
export const MAX_INGESTION_FIELDS = 40;

export interface WorkflowIngestionFieldLike {
  readonly name: string;
  readonly description: string;
}

/**
 * The fields an Ingestion step reads, names trimmed, in its order. Anything
 * that is not a field is dropped; a blank or clashing name stays, so the graph
 * check can name it.
 */
export function workflowIngestionFields(node: WorkflowGraphNodeLike): ReadonlyArray<WorkflowIngestionFieldLike> {
  const config = node.config as { readonly fields?: unknown } | undefined;
  const raw = Array.isArray(config?.fields) ? config.fields : [];
  return raw.flatMap((entry: unknown) => {
    const field = entry as { readonly name?: unknown; readonly description?: unknown } | null;
    if (!field || typeof field !== "object" || typeof field.name !== "string") return [];
    return [{ name: field.name.trim(), description: typeof field.description === "string" ? field.description.trim() : "" }];
  });
}

/** Why an Ingestion step's fields cannot run; null when they can. */
const ingestionIssue = (node: WorkflowGraphNodeLike): WorkflowGraphIssueLike | null => {
  const fields = workflowIngestionFields(node);
  const at = { nodeId: node.id };
  if (fields.length === 0) return issue("empty-ingestion", "List the fields this step reads.", at);
  if (fields.length > MAX_INGESTION_FIELDS) {
    return issue("too-many-fields", `Name at most ${MAX_INGESTION_FIELDS} fields on one step.`, at);
  }
  if (fields.some((field) => field.name === "")) return issue("field-name", "Every field needs a name.", at);
  // A dot separates the step from the field in `{{Step.Field}}`; braces end a reference.
  if (fields.some((field) => /[.{}]/u.test(field.name))) {
    return issue("field-name", "A field name cannot contain a dot or braces.", at);
  }
  const names = new Set(fields.map((field) => foldLabel(field.name)));
  if (names.size !== fields.length) return issue("duplicate-field", "Two fields share a name. Give each its own.", at);
  return null;
};

/** Loose on purpose: one @, something either side, a dot inside the domain, no spaces. */
const looksLikeEmail = (email: string): boolean => {
  const [local, domain, extra] = email.split("@");
  if (extra !== undefined || !local || !domain || /\s/u.test(email)) return false;
  const dot = domain.indexOf(".");
  return dot > 0 && dot < domain.length - 1;
};

/** Whether an address looks like one, by the same loose rule the graph check uses for approvers. */
export const workflowLooksLikeEmail = (email: string): boolean => looksLikeEmail(email);

/**
 * Everything that stops this graph from running, each attached to the step
 * or link it is about. An empty list means the graph can run.
 */
export function validateWorkflowGraph(graph: WorkflowGraphLike): ReadonlyArray<WorkflowGraphIssueLike> {
  const issues: WorkflowGraphIssueLike[] = [];
  const nodes = new Map<string, WorkflowGraphNodeLike>();
  for (const node of graph.nodes) {
    if (nodes.has(node.id)) {
      issues.push(issue("duplicate-node", "Two steps share an id.", { nodeId: node.id }));
      continue;
    }
    nodes.set(node.id, node);
  }

  const starts = graph.nodes.filter((node) => node.type === "start");
  if (starts.length === 0) issues.push(issue("no-start", "Add a Start step: every run begins there."));
  for (const extra of starts.slice(1)) {
    issues.push(issue("extra-start", "A workflow has one Start step. Remove this one.", { nodeId: extra.id }));
  }
  if (!graph.nodes.some((node) => node.type !== "start")) {
    issues.push(issue("empty", "Add a step after Start."));
  }

  const edgeIds = new Set<string>();
  const usable: WorkflowGraphEdgeLike[] = [];
  const pairs = new Set<string>();
  for (const edge of graph.edges) {
    if (edgeIds.has(edge.id)) {
      issues.push(issue("duplicate-edge", "Two links share an id.", { edgeId: edge.id }));
      continue;
    }
    edgeIds.add(edge.id);
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    if (!source || !target) {
      issues.push(issue("dangling-edge", "This link points at a step that no longer exists.", { edgeId: edge.id }));
      continue;
    }
    if (edge.source === edge.target) {
      issues.push(issue("self-link", "A step cannot link to itself.", { edgeId: edge.id }));
      continue;
    }
    if (target.type === "start") {
      issues.push(issue("into-start", "Nothing can link into Start.", { edgeId: edge.id }));
      continue;
    }
    if (source.type === "output") {
      issues.push(issue("from-output", "Output ends a path; nothing can follow it.", { edgeId: edge.id }));
      continue;
    }
    // A Condition or a Compare step leaves only by "yes" or "no"; every other step has one plain outlet.
    if (workflowStepBranches(source.type)) {
      if (!edge.sourceHandle || !(CONDITION_OUTLETS as ReadonlyArray<string>).includes(edge.sourceHandle)) {
        const what = source.type === "compare" ? "Compare step" : "Condition";
        issues.push(issue("condition-outlet", `A link from a ${what} must leave by Yes or No.`, { edgeId: edge.id }));
        continue;
      }
    } else if (edge.sourceHandle !== undefined) {
      issues.push(issue("stray-outlet", "Only a Condition or a Compare step has Yes and No outlets.", { edgeId: edge.id }));
      continue;
    }
    const pair = `${edge.source}\u0000${edge.sourceHandle ?? ""}\u0000${edge.target}`;
    if (pairs.has(pair)) {
      issues.push(issue("duplicate-link", "These two steps are already linked.", { edgeId: edge.id }));
      continue;
    }
    pairs.add(pair);
    usable.push(edge);
  }

  for (const nodeId of findCycleNodes([...nodes.keys()], usable)) {
    issues.push(issue("cycle", "This step is part of a loop. Links must only go forward.", { nodeId }));
  }

  const start = starts[0];
  if (start) {
    const reached = reachableFrom(start.id, usable);
    for (const node of nodes.values()) {
      if (!reached.has(node.id)) {
        issues.push(issue("unreachable", "Nothing leads to this step from Start.", { nodeId: node.id }));
      }
    }
  }

  for (const node of nodes.values()) {
    if (node.type === "prompt" && promptInstructions(node).trim() === "") {
      issues.push(issue("empty-prompt", "Write the instructions for this step.", { nodeId: node.id }));
    }
    if (node.type === "browser-task" && browserTaskId(node).trim() === "") {
      issues.push(issue("no-browser-task", "Choose the browser task this step runs.", { nodeId: node.id }));
    }
    if (node.type === "condition") {
      if (!conditionIsSet(node)) {
        issues.push(issue("empty-condition", "Say what this Condition decides on.", { nodeId: node.id }));
      }
      if (!usable.some((edge) => edge.source === node.id)) {
        issues.push(issue("condition-dead-end", "Link Yes or No to the step that follows.", { nodeId: node.id }));
      }
    }
    if (node.type === "compare") {
      const compare = workflowCompareConfig(node);
      const before = new Set(workflowAncestors(graph, node.id));
      if (!compare.left.step || !compare.right.step) {
        issues.push(issue("compare-tables", "Choose the two tables this step compares.", { nodeId: node.id }));
      } else if (!before.has(compare.left.step) || !before.has(compare.right.step)) {
        issues.push(issue("compare-tables", "Compare tables from steps that come before this one.", { nodeId: node.id }));
      }
      if (!compare.key) issues.push(issue("compare-key", "Name the column rows are matched on.", { nodeId: node.id }));
      if (compare.rules.length === 0 && !compare.missing) {
        issues.push(issue("compare-rules", "Add a rule, or flag rows only one table has.", { nodeId: node.id }));
      } else if (compare.rules.length > MAX_COMPARE_RULES) {
        issues.push(issue("compare-rules", `Check at most ${MAX_COMPARE_RULES} rules in one step.`, { nodeId: node.id }));
      } else if (compare.rules.some((rule) => !rule.column)) {
        issues.push(issue("compare-rules", "Name the column every rule checks.", { nodeId: node.id }));
      }
      if (!usable.some((edge) => edge.source === node.id)) {
        issues.push(issue("condition-dead-end", "Link Yes or No to the step that follows.", { nodeId: node.id }));
      }
    }
    if (node.type === "approval") {
      const approvers = workflowApproverEmails(node);
      if (approvers.length === 0) {
        issues.push(issue("empty-approval", "Choose who approves this step.", { nodeId: node.id }));
      } else if (approvers.length > MAX_APPROVERS) {
        issues.push(issue("too-many-approvers", `Name at most ${MAX_APPROVERS} approvers on one step.`, { nodeId: node.id }));
      } else if (!approvers.every(looksLikeEmail)) {
        issues.push(issue("approver-email", "One of the approvers is not an email address.", { nodeId: node.id }));
      }
    }
    if (node.type === "for-each") {
      const config = (node.config ?? {}) as { readonly source?: { readonly step?: unknown }; readonly instructions?: unknown };
      const source = typeof config.source?.step === "string" ? config.source.step.trim() : "";
      if (!source) {
        issues.push(issue("for-each-table", "Choose the table whose rows this step goes through.", { nodeId: node.id }));
      } else if (!workflowAncestors(graph, node.id).includes(source)) {
        issues.push(issue("for-each-table", "Go through a table from a step that comes before this one.", { nodeId: node.id }));
      }
      if (typeof config.instructions !== "string" || !config.instructions.trim()) {
        issues.push(issue("empty-prompt", "Say what to do for each row.", { nodeId: node.id }));
      }
    }
    if (node.type === "checklist") {
      const people = workflowChecklistEmails(node);
      const instructions = (node.config as { readonly instructions?: unknown } | undefined)?.instructions;
      if (typeof instructions !== "string" || !instructions.trim()) {
        issues.push(issue("empty-checklist", "Say what has to be done at this step.", { nodeId: node.id }));
      }
      if (people.length > MAX_APPROVERS) {
        issues.push(issue("too-many-approvers", `Name at most ${MAX_APPROVERS} people on one step.`, { nodeId: node.id }));
      } else if (!people.every(looksLikeEmail)) {
        issues.push(issue("approver-email", "One of the people named is not an email address.", { nodeId: node.id }));
      }
    }
    if (node.type === "notify" || node.type === "email") {
      const recipients = workflowRecipientEmails(node);
      const what = node.type === "email" ? "gets this email" : "gets this notice";
      if (recipients.length === 0) {
        issues.push(issue("empty-recipients", `Choose who ${what}.`, { nodeId: node.id }));
      } else if (recipients.length > MAX_RECIPIENTS) {
        issues.push(issue("too-many-recipients", `Name at most ${MAX_RECIPIENTS} people on one step.`, { nodeId: node.id }));
      } else if (!recipients.every(looksLikeEmail)) {
        issues.push(issue("recipient-email", "One of the people is not an email address.", { nodeId: node.id }));
      }
    }
    if (node.type === "intake") {
      const problem = intakeIssue(node);
      if (problem) issues.push(problem);
    }
    if (node.type === "save-file") {
      const config = node.config as { readonly spaceId?: unknown } | undefined;
      if (typeof config?.spaceId !== "string" || config.spaceId.trim() === "") {
        issues.push(issue("no-space", "Choose the space to save into.", { nodeId: node.id }));
      }
    }
    if (node.type === "ingestion") {
      const problem = ingestionIssue(node);
      if (problem) issues.push(problem);
    }
  }

  // `{{Step name}}` finds a step by its name, so names must tell steps apart,
  // and `{{input}}` is the run's input, never a step.
  const named = new Set<string>();
  for (const node of nodes.values()) {
    const name = foldLabel(node.label);
    if (name === "input") {
      issues.push(issue("reserved-name", "\"Input\" means the run's input. Give this step another name.", { nodeId: node.id }));
    } else if (named.has(name)) {
      issues.push(issue("duplicate-name", "Another step has this name. Give each step its own name.", { nodeId: node.id }));
    }
    named.add(name);
  }
  return issues;
}

function reachableFrom(startId: string, edges: ReadonlyArray<WorkflowGraphEdgeLike>): Set<string> {
  const next = new Map<string, string[]>();
  for (const edge of edges) next.set(edge.source, [...(next.get(edge.source) ?? []), edge.target]);
  const seen = new Set<string>([startId]);
  const queue = [startId];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    for (const target of next.get(id) ?? []) {
      if (!seen.has(target)) {
        seen.add(target);
        queue.push(target);
      }
    }
  }
  return seen;
}

/**
 * Steps that sit on a loop: members of a strongly connected component with
 * more than one step (Tarjan). Steps merely downstream of a loop are not on
 * it, so they are not blamed. Self-links are refused before this runs.
 */
function findCycleNodes(nodeIds: ReadonlyArray<string>, edges: ReadonlyArray<WorkflowGraphEdgeLike>): string[] {
  const next = new Map<string, string[]>();
  for (const edge of edges) next.set(edge.source, [...(next.get(edge.source) ?? []), edge.target]);
  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const onLoop: string[] = [];
  let counter = 0;
  const visit = (id: string): void => {
    index.set(id, counter);
    low.set(id, counter);
    counter += 1;
    stack.push(id);
    onStack.add(id);
    for (const target of next.get(id) ?? []) {
      if (!index.has(target)) {
        visit(target);
        low.set(id, Math.min(low.get(id) as number, low.get(target) as number));
      } else if (onStack.has(target)) {
        low.set(id, Math.min(low.get(id) as number, index.get(target) as number));
      }
    }
    if (low.get(id) === index.get(id)) {
      const component: string[] = [];
      let member: string | undefined;
      do {
        member = stack.pop() as string;
        onStack.delete(member);
        component.push(member);
      } while (member !== id);
      if (component.length > 1) onLoop.push(...component);
    }
  };
  // Graphs are capped at 100 steps, so recursion depth is bounded.
  for (const id of nodeIds) if (!index.has(id)) visit(id);
  return nodeIds.filter((id) => onLoop.includes(id));
}

/** Steps in an order where every step comes after the steps that link into it. */
export function workflowTopologicalOrder(graph: WorkflowGraphLike): ReadonlyArray<string> {
  const ids = graph.nodes.map((node) => node.id);
  const known = new Set(ids);
  const edges = graph.edges.filter((edge) => known.has(edge.source) && known.has(edge.target));
  const indegree = new Map(ids.map((id) => [id, 0]));
  const next = new Map<string, string[]>();
  for (const edge of edges) {
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    next.set(edge.source, [...(next.get(edge.source) ?? []), edge.target]);
  }
  const order: string[] = [];
  const queue = ids.filter((id) => indegree.get(id) === 0);
  while (queue.length > 0) {
    const id = queue.shift() as string;
    order.push(id);
    for (const target of next.get(id) ?? []) {
      const remaining = (indegree.get(target) ?? 0) - 1;
      indegree.set(target, remaining);
      if (remaining === 0) queue.push(target);
    }
  }
  // A loop's steps never reach zero; keep them, last, so nothing disappears.
  for (const id of ids) if (!order.includes(id)) order.push(id);
  return order;
}

/** Every step that leads, directly or not, to `nodeId`, in run order. */
export function workflowAncestors(graph: WorkflowGraphLike, nodeId: string): ReadonlyArray<string> {
  const previous = new Map<string, string[]>();
  for (const edge of graph.edges) previous.set(edge.target, [...(previous.get(edge.target) ?? []), edge.source]);
  const seen = new Set<string>();
  const stack = [...(previous.get(nodeId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (seen.has(id) || id === nodeId) continue;
    seen.add(id);
    stack.push(...(previous.get(id) ?? []));
  }
  return workflowTopologicalOrder(graph).filter((id) => seen.has(id));
}

/** `nodeId` and every step after it: what a retry from `nodeId` runs again. */
export function workflowDescendants(graph: WorkflowGraphLike, nodeId: string): ReadonlySet<string> {
  return reachableFrom(nodeId, graph.edges);
}

const SETTLED: ReadonlySet<WorkflowStepState> = new Set(["succeeded", "failed", "skipped", "cancelled"]);

type LinkState = "unsettled" | "active" | "inactive" | "blocked";

function linkState(edge: WorkflowGraphEdgeLike, source: WorkflowStepFacts | undefined): LinkState {
  if (!source || !SETTLED.has(source.status)) return "unsettled";
  if (source.status === "failed" || source.status === "cancelled") return "blocked";
  if (source.status === "skipped") return "inactive";
  const chosen = source.chosenHandles;
  if (chosen === null || chosen === undefined) return "active";
  return edge.sourceHandle !== undefined && chosen.includes(edge.sourceHandle) ? "active" : "inactive";
}

/**
 * Whether a run went down a link: its step finished and chose the link's
 * outlet (a Condition takes Yes or No; every other step its one outlet).
 * The chart draws a run's path with it, by the runner's own rule.
 */
export const workflowLinkTaken = (edge: WorkflowGraphEdgeLike, source: WorkflowStepFacts | undefined): boolean =>
  linkState(edge, source) === "active";

/** Whether a link can no longer carry the run: its step was skipped, or chose the other outlet. */
export const workflowLinkNotTaken = (edge: WorkflowGraphEdgeLike, source: WorkflowStepFacts | undefined): boolean =>
  linkState(edge, source) === "inactive";

export interface WorkflowRunPlan {
  /** Pending steps whose inputs are all in: start these. */
  readonly ready: ReadonlyArray<string>;
  /** Pending steps on a branch not taken, including what follows them. */
  readonly skip: ReadonlyArray<string>;
  /** How the run stands once `skip` is applied and `ready` has started. */
  readonly state: WorkflowRunState;
}

/**
 * The next move for a run. Steps missing from `steps` count as pending. The
 * caller applies `skip`, starts `ready`, and records `state`; calling again
 * after any step settles gives the next move.
 */
export function planWorkflowRun(
  graph: WorkflowGraphLike,
  steps: ReadonlyMap<string, WorkflowStepFacts>,
): WorkflowRunPlan {
  const facts = new Map<string, WorkflowStepFacts>();
  for (const node of graph.nodes) facts.set(node.id, steps.get(node.id) ?? { status: "pending" });
  const incoming = new Map<string, WorkflowGraphEdgeLike[]>();
  for (const edge of graph.edges) incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge]);

  const skip: string[] = [];
  const ready: string[] = [];
  // Skipping one step can settle the links into the next, so repeat until still.
  let changed = true;
  while (changed) {
    changed = false;
    for (const nodeId of workflowTopologicalOrder(graph)) {
      const step = facts.get(nodeId);
      if (!step || step.status !== "pending" || ready.includes(nodeId)) continue;
      const links = incoming.get(nodeId) ?? [];
      if (links.length === 0) {
        // Start (or a step nothing leads to, which validation refuses): runs first.
        ready.push(nodeId);
        continue;
      }
      const states = links.map((edge) => linkState(edge, facts.get(edge.source)));
      if (states.includes("unsettled") || states.includes("blocked")) continue;
      if (states.includes("active")) {
        ready.push(nodeId);
      } else {
        facts.set(nodeId, { status: "skipped" });
        skip.push(nodeId);
        changed = true;
      }
    }
  }

  const statuses = [...facts.values()].map((step) => step.status);
  const state: WorkflowRunState =
    ready.length > 0 || statuses.includes("running")
      ? "running"
      : statuses.includes("waiting")
        ? "waiting"
        : statuses.includes("failed") || statuses.includes("cancelled")
          ? "failed"
          : statuses.includes("pending")
            ? // Nothing can move and nothing failed: only a malformed graph gets here.
              "failed"
            : "succeeded";
  return { ready, skip, state };
}

/* ------------------------------------------------------------------ */
/* Prompt composition                                                  */
/* ------------------------------------------------------------------ */

/** The most of one earlier step's output a later prompt carries. */
export const WORKFLOW_CONTEXT_PER_STEP = 60_000;
/** The most a whole prompt carries, instructions included. */
export const WORKFLOW_PROMPT_BUDGET = 200_000;

/** One field an Ingestion step read; null when it was not found. */
export interface WorkflowStepFieldLike {
  readonly name: string;
  readonly value: string | null;
}

export interface WorkflowPromptSource {
  readonly label: string;
  readonly output: string;
  /** An Ingestion step's fields, for `{{Step name.Field name}}`. */
  readonly fields?: ReadonlyArray<WorkflowStepFieldLike> | undefined;
}

// No optional spaces in the pattern itself (ambiguous repetition is slow on
// crafted input); a reference's name is trimmed after it matches.
const REFERENCE = /\{\{([^{}]{1,120})\}\}/g;
const foldLabel = (label: string) => label.trim().toLowerCase().replace(/\s+/g, " ");

const clip = (text: string, limit: number) =>
  text.length <= limit ? text : `${text.slice(0, limit)}\n[… ${text.length - limit} more characters not shown]`;

/** The `{{…}}` references in instructions, as written, without duplicates. */
export function workflowPromptReferences(instructions: string): ReadonlyArray<string> {
  const found: string[] = [];
  for (const match of instructions.matchAll(REFERENCE)) {
    const name = (match[1] ?? "").trim();
    if (name && !found.some((existing) => foldLabel(existing) === foldLabel(name))) found.push(name);
  }
  return found;
}

/**
 * What a reference names among the earlier steps: a step's output by its
 * name, or, failing that, `Step name.Field name` (split at the last dot, as a
 * field name has none) one of an Ingestion step's fields. Empty when it names
 * nothing, or a field that was not found.
 */
const resolveReference = (key: string, upstream: ReadonlyArray<WorkflowPromptSource>): string => {
  const step = upstream.find((source) => foldLabel(source.label) === key);
  if (step) return step.output;
  const dot = key.lastIndexOf(".");
  if (dot <= 0) return "";
  const owner = upstream.find((source) => foldLabel(source.label) === key.slice(0, dot).trim());
  const fieldKey = key.slice(dot + 1).trim();
  return owner?.fields?.find((field) => foldLabel(field.name) === fieldKey)?.value ?? "";
};

/**
 * The message a Prompt step sends. `{{input}}` is the run's input,
 * `{{Step name}}` an earlier step's output and `{{Step name.Field name}}` one
 * of an earlier Ingestion step's fields (names match case-insensitively).
 * Instructions that reference nothing get the input and every earlier output
 * appended, so a plain prompt still sees what came before it. A reference to
 * a step that produced nothing (skipped, or not upstream) reads as empty.
 */
export function composeWorkflowPrompt(input: {
  readonly instructions: string;
  readonly runInput: string | null;
  readonly upstream: ReadonlyArray<WorkflowPromptSource>;
}): string {
  const runInput = input.runInput?.trim() ?? "";
  const references = workflowPromptReferences(input.instructions);
  if (references.length > 0) {
    const text = input.instructions.replace(REFERENCE, (_match, name: string) => {
      const key = foldLabel(name);
      if (key === "input") return runInput;
      return clip(resolveReference(key, input.upstream), WORKFLOW_CONTEXT_PER_STEP);
    });
    return clip(text, WORKFLOW_PROMPT_BUDGET);
  }
  const sections = [input.instructions.trim()];
  if (runInput) sections.push(`## Input\n\n${runInput}`);
  for (const source of input.upstream) {
    if (source.output.trim()) {
      sections.push(`## ${source.label}\n\n${clip(source.output.trim(), WORKFLOW_CONTEXT_PER_STEP)}`);
    }
  }
  return clip(sections.join("\n\n"), WORKFLOW_PROMPT_BUDGET);
}

/**
 * An Ingestion step's template with each `{{Field name}}` filled (names match
 * case-insensitively). A field that was not found fills as empty; a
 * placeholder that names no field stays as written, so the gap shows.
 */
export function fillWorkflowTemplate(template: string, fields: ReadonlyArray<WorkflowStepFieldLike>): string {
  return template.replace(REFERENCE, (match, name: string) => {
    const field = fields.find((entry) => foldLabel(entry.name) === foldLabel(name));
    return field ? (field.value ?? "") : match;
  });
}
